/**
 * Top up voice credits — Two-step flow: 1 Configure → 2 Review & Pay
 *
 * On Add Credits click: getProformaUnits('add_credits') is called; modal opens with units (per_credit_cost, tax_rate).
 * Step 1: Number of credits (min 1000, max 999999, step 500 for stepper; any value allowed in input).
 *         UNIT PRICE, ESTIMATED TOTAL, proration/carry-over message, "Continue to Review".
 * Step 2: Transaction Summary (Voice Credits, GST, Total Payable), "1 credit = 1 minute...", Confirm → Pay Now.
 * Flow: Continue to Review (fetch proforma) → Confirm (payment link) → Pay Now → success/retry modal with polling.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Add as PlusIcon,
  Remove as MinusIcon,
  InfoOutlined as InfoIcon,
  RecordVoiceOver as MicIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';

const MIN_CREDITS = 1000;
const MAX_CREDITS = 999999;
const STEP_CREDITS = 500;

const neutral = {
  50: '#fafafa',
  200: '#e4e4e7',
  400: '#a1a1aa',
  500: '#71717a',
  600: '#52525b',
  700: '#3f3f46',
  800: '#27272a',
  900: '#18181b',
};

const ProfarmaInvoiceModalAddCredits = ({
  open,
  onClose,
  unitsData,
  data,
  loading,
  error,
  paymentUrl,
  onConfirm,
  confirmLoading,
  confirmError,
  onPayNow,
  onCreditsChange,
  onBack,
}) => {
  const formatCurrency = (amount, currency = 'INR', decimals = 2) => {
    const symbol = currency === 'INR' ? '₹' : '$';
    return `${symbol}${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };

  const units = unitsData?.units ?? unitsData?.data ?? {};
  const perCreditCost = Number(units.effective_per_credit_cost ?? units.per_credit_cost ?? 0);
  const standardPerCreditCost = Number(units.per_credit_cost ?? 0);
  const taxRate = Number(data?.tax_rate ?? units.tax_rate ?? 0.18);
  const currency = data?.currency ?? units.currency ?? 'INR';
  const remainingDays = units.remaining_days ?? null;

  const lineItems = data?.particulars || data?.line_items || [];
  const hasProformaData = lineItems.length > 0;
  const currentCreditsFromProforma = lineItems[0]?.quantity ?? MIN_CREDITS;

  const hasUnitsData = !!(units.per_credit_cost != null || units.effective_per_credit_cost != null);

  const [creditsInput, setCreditsInput] = useState(String(MIN_CREDITS));
  const [step, setStep] = useState(1);
  const [pendingStep2, setPendingStep2] = useState(false);

  useEffect(() => {
    if (open) {
      setCreditsInput(hasProformaData ? String(currentCreditsFromProforma) : String(MIN_CREDITS));
      setStep(1);
      setPendingStep2(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && step === 2 && hasProformaData) setCreditsInput(String(currentCreditsFromProforma));
  }, [open, step, hasProformaData, currentCreditsFromProforma]);

  useEffect(() => {
    if (error && pendingStep2) setPendingStep2(false);
  }, [error, pendingStep2]);

  const creditsQty = Math.max(MIN_CREDITS, Math.min(MAX_CREDITS, parseInt(creditsInput, 10) || MIN_CREDITS));

  const subtotalFromFormula = creditsQty * perCreditCost;
  const subtotalDisplay = step === 2 && data?.subtotal != null ? Number(data.subtotal) : subtotalFromFormula;
  const totalDisplay = step === 2 && data?.total != null ? Number(data.total) : subtotalDisplay + (subtotalDisplay * taxRate);
  // Tax: use backend value when present and > 0; else derive from total - subtotal in step 2; else compute from rate
  const backendTax = step === 2 && (data?.tax_amount != null || data?.tax != null) ? Number(data.tax_amount ?? data.tax ?? 0) : null;
  const taxDisplay = step === 2 && data?.total != null && data?.subtotal != null && (backendTax == null || backendTax === 0)
    ? (Number(data.total) - Number(data.subtotal))
    : (backendTax != null && backendTax > 0 ? backendTax : subtotalDisplay * taxRate);

  const handleCreditsBlur = () => {
    const n = parseInt(creditsInput, 10);
    if (Number.isNaN(n) || n < MIN_CREDITS) setCreditsInput(String(MIN_CREDITS));
    else if (n > MAX_CREDITS) setCreditsInput(String(MAX_CREDITS));
  };

  const handleContinueToReview = () => {
    const n = parseInt(creditsInput, 10);
    const qty = Number.isNaN(n) || n < MIN_CREDITS ? MIN_CREDITS : Math.min(MAX_CREDITS, n);
    setCreditsInput(String(qty));
    setStep(2);
    setPendingStep2(true);
    if (onCreditsChange) onCreditsChange(qty, () => setPendingStep2(false));
  };

  const handleBack = () => {
    setStep(1);
    setPendingStep2(false);
    if (onBack) onBack();
  };

  const handleCreditsKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (step === 1) handleContinueToReview();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
    >
      <Box sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: '#fff', flex: 1 }}>
        {/* Header */}
        <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: neutral[200], flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: neutral[900], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Top up voice credits
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: neutral[500], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Purchase additional credits for AI-powered insights.
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small"><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
          </Box>
        </Box>

        {loading && !hasUnitsData && (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, py: 6 }}>
            <CircularProgress size={36} sx={{ color: 'primary.main' }} />
            <Typography sx={{ mt: 2, fontSize: '0.875rem', color: neutral[600], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Loading…</Typography>
          </Box>
        )}

        {error && !hasUnitsData && (
          <Box sx={{ px: 2.5, py: 3 }}>
            <Alert severity="error" sx={{ fontSize: '0.75rem' }}>{error}</Alert>
          </Box>
        )}

        {/* Step 1: Configure */}
        {step === 1 && hasUnitsData && (
          <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5, flex: 1, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12), border: '1px solid', borderColor: 'primary.light' }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>1</Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'primary.dark', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Configure</Typography>
              </Box>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: 2, bgcolor: neutral[50], border: '1px solid', borderColor: neutral[200] }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: neutral[400], color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>2</Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: neutral[600], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Review & Pay</Typography>
              </Box>
            </Box>

            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: neutral[900], mb: 1.5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Number of credits
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <TextField
                size="small"
                type="number"
                value={creditsInput}
                onChange={(e) => setCreditsInput(e.target.value)}
                onBlur={handleCreditsBlur}
                onKeyDown={handleCreditsKeyDown}
                inputProps={{ min: MIN_CREDITS, max: MAX_CREDITS, step: STEP_CREDITS }}
                InputProps={{
                  startAdornment: <MicIcon sx={{ color: neutral[500], mr: 1, fontSize: 20 }} />,
                }}
                sx={{ maxWidth: 160, '& .MuiInputBase-input': { fontSize: '1rem', py: 1 } }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  onClick={() => setCreditsInput(String(Math.max(MIN_CREDITS, creditsQty - STEP_CREDITS)))}
                  disabled={creditsQty <= MIN_CREDITS}
                  sx={{ width: 36, height: 36, border: '1px solid', borderColor: neutral[200], borderRadius: 1.5, '&:hover': { borderColor: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) }, '&:disabled': { opacity: 0.5 } }}
                >
                  <MinusIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  onClick={() => setCreditsInput(String(Math.min(MAX_CREDITS, creditsQty + STEP_CREDITS)))}
                  disabled={creditsQty >= MAX_CREDITS}
                  sx={{ width: 36, height: 36, border: '1px solid', borderColor: neutral[200], borderRadius: 1.5, '&:hover': { borderColor: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) }, '&:disabled': { opacity: 0.5 } }}
                >
                  <PlusIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: neutral[500], mb: 2, display: 'flex', alignItems: 'center', gap: 0.5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <InfoIcon sx={{ fontSize: 14 }} /> Minimum purchase: {MIN_CREDITS.toLocaleString('en-IN')} credits.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12), border: '1px solid', borderColor: 'primary.light' }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'primary.dark', textTransform: 'uppercase', mb: 0.5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Unit price</Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: 'primary.main', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {formatCurrency(perCreditCost, currency)}
                  <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'primary.dark', ml: 0.5 }}>/ credit</Typography>
                </Typography>
                {standardPerCreditCost > 0 && standardPerCreditCost !== perCreditCost && (
                  <Typography sx={{ fontSize: '0.7rem', color: 'primary.dark', mt: 0.25, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Standard: {formatCurrency(standardPerCreditCost, currency)}</Typography>
                )}
              </Box>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: neutral[50], border: '1px solid', borderColor: neutral[200] }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: neutral[700], textTransform: 'uppercase', mb: 0.5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Estimated total</Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: neutral[900], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {formatCurrency(subtotalFromFormula, currency)}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: neutral[600], mt: 0.25, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>+ taxes ({(taxRate * 100).toFixed(0)}%)</Typography>
              </Box>
            </Box>

            {remainingDays != null && remainingDays > 0 && (
              <Alert
                icon={<InfoIcon />}
                severity="info"
                sx={{ mb: 2, fontSize: '0.75rem', py: 0.75, fontFamily: "'Plus Jakarta Sans', sans-serif", '& .MuiAlert-message': { width: '100%' } }}
              >
                Your credits are pro-rated based on the <strong>{remainingDays} days</strong> left in your current cycle.
              </Alert>
            )}

            {/* Carry-over message (from product policy) */}
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#ecfdf5', border: '1px solid', borderColor: '#a7f3d0', mb: 2 }}>
              <Typography sx={{ fontSize: '0.8125rem', color: '#065f46', fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.5 }}>
                <strong>Unused credits are retained when your subscription renews</strong> 
              </Typography>
            </Box>

            {/* Summary: purchasing X credits */}
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: neutral[900], mb: 1.5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Purchasing {creditsQty.toLocaleString('en-IN')} voice credits.
            </Typography>

            {/* Pointers — all values from backend (units.data / proforma): per_credit_cost, tax_rate */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: neutral[50],
                border: '1px solid',
                borderColor: neutral[200],
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: neutral[500], textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.25, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Summary
              </Typography>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', '& > li': { display: 'flex', alignItems: 'center', gap: 1, py: 0.6, fontSize: '0.8125rem', color: neutral[700], fontFamily: "'Plus Jakarta Sans', sans-serif" } }}>
                <li>
                  <CheckIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                  Minimum credit to purchase: 1,000
                </li>
                <li>
                  <CheckIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                  Per voice credit cost: {formatCurrency(perCreditCost, currency)}
                </li>
                <li>
                  <CheckIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                  1 credit = 1 minute call AI-Insights
                </li>
                <li>
                  <CheckIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                  Tax (GST): {(taxRate * 100).toFixed(0)}% applied on subtotal
                </li>
              </Box>
            </Box>

            <Button
              variant="contained"
              onClick={handleContinueToReview}
              disabled={creditsQty < MIN_CREDITS}
              fullWidth
              sx={{
                py: 1.25,
                fontSize: '0.9375rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                bgcolor: 'primary.main',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              Continue to Review
            </Button>
          </Box>
        )}

        {/* Step 2: Review & Pay */}
        {step === 2 && !pendingStep2 && (
          <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5, flex: 1, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: 2, bgcolor: neutral[50], border: '1px solid', borderColor: neutral[200] }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckIcon sx={{ fontSize: 12 }} />
                </Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: neutral[600], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Configure</Typography>
              </Box>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12), border: '1px solid', borderColor: 'primary.light' }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>2</Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'primary.dark', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Review & Pay</Typography>
              </Box>
            </Box>

            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: neutral[900], mb: 1.5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Transaction Summary
            </Typography>

            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.8125rem', color: neutral[700], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Voice Credits ({creditsQty.toLocaleString('en-IN')} × {formatCurrency(perCreditCost, currency)})
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: neutral[900], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {formatCurrency(subtotalDisplay, currency)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.8125rem', color: neutral[700], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  GST ({(taxRate * 100).toFixed(0)}%)
                </Typography>
                <Tooltip title={`Tax rate: ${(taxRate * 100).toFixed(0)}% applied on taxable amount`} placement="top">
                  <InfoIcon sx={{ fontSize: 14, color: neutral[400] }} />
                </Tooltip>
              </Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: neutral[900], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {formatCurrency(taxDisplay, currency)}
              </Typography>
            </Box>
            <Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: neutral[200] }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: neutral[900], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Total Payable
                </Typography>
                <Typography sx={{ fontSize: '1.375rem', fontWeight: 800, color: 'primary.main', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {formatCurrency(totalDisplay, currency)}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.7rem', color: neutral[500], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Includes all applicable taxes
              </Typography>
            </Box>

            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#ecfdf5', border: '1px solid', borderColor: '#a7f3d0', mt: 2, mb: 2, display: 'flex', gap: 1 }}>
              <BoltIcon sx={{ color: '#059669', fontSize: 22, flexShrink: 0, mt: 0.25 }} />
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#065f46', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                1 credit = 1 minute of call AI-Insights
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={confirmLoading}
                sx={{
                  flex: '0 0 auto',
                  py: 1.125,
                  px: 2.5,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  borderColor: neutral[300],
                  color: neutral[700],
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  '&:hover': { borderColor: neutral[400], bgcolor: neutral[50] },
                }}
              >
                Back
              </Button>
              {!paymentUrl ? (
                <Button
                  variant="contained"
                  onClick={onConfirm}
                  disabled={confirmLoading}
                  fullWidth
                  sx={{
                    py: 1.125,
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  {confirmLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirm'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={onPayNow}
                  disabled={confirmLoading}
                  fullWidth
                  sx={{
                    py: 1.125,
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  Pay Now — {formatCurrency(totalDisplay, currency)}
                </Button>
              )}
            </Box>

            {confirmError && (
              <Alert severity="error" sx={{ mt: 2, fontSize: '0.75rem', py: 0.5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {confirmError}
              </Alert>
            )}
          </Box>
        )}

        {pendingStep2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, py: 4 }}>
            <CircularProgress size={32} sx={{ color: 'primary.main' }} />
            <Typography sx={{ mt: 1.5, fontSize: '0.8125rem', color: neutral[600], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Loading invoice…
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default ProfarmaInvoiceModalAddCredits;
